import {
  getActivityEditTargetId,
  getActivityModelTargetId,
  getBankAccountEditTargetId,
  getEventModelTargetId,
  getFinancialCreateFlow,
  getFinancialEditTarget,
  shouldLeaveFinancialEditOnCancel,
  getInventoryEditTargetId,
  getMonthlyFeeEditTargetId,
  getRitualBathEditTargetId,
  isBankAccountCreateRoute,
  isRitualBathCreateRoute,
} from "./formChatRoutes";
import type { ActivityType } from "../types/activity.types";
import { getActivityTypeFromPath } from "../utils/activityTypeRoutes";

export {
  getActivityEditTargetId,
  getActivityModelTargetId,
  getBankAccountEditTargetId,
  getEventModelTargetId,
  getFinancialCreateFlow,
  getFinancialEditTarget,
  shouldLeaveFinancialEditOnCancel,
  getInventoryEditTargetId,
  getMonthlyFeeEditTargetId,
  getRitualBathEditTargetId,
};

export type FormChatContextKey =
  | "inventory_item"
  | "inventory_edit"
  | "activity"
  | "activity_edit"
  | "activity_model"
  | "event"
  | "event_model"
  | "spiritual_profile"
  | "income"
  | "income_edit"
  | "outcome"
  | "outcome_edit"
  | "monthly_fee_edit"
  | "bank_account"
  | "bank_account_edit"
  | "ritual_bath"
  | "ritual_bath_edit";

export interface FormChatContextConfig {
  key: FormChatContextKey;
  entityType:
    | "inventory_item"
    | "activity"
    | "event"
    | "spiritual_profile"
    | "income"
    | "outcome"
    | "monthly_fee"
    | "bank_account"
    | "ritual_bath";
  title: string;
  subtitle: string;
  initialMessage: string;
  initialActiveField?: string;
  confirmLabel: string;
  requiredPermission: string;
  requiredPermissionOwn?: string;
  /** Activity flavor for `entityType: "activity"`; sent to the API and stamped on the payload. */
  activityType?: ActivityType;
}

const INVENTORY_CONTEXT: FormChatContextConfig = {
  key: "inventory_item",
  entityType: "inventory_item",
  title: "Assistente de estoque",
  subtitle: "Cadastro conversacional de estoque",
  initialMessage:
    "Oi! Vamos cadastrar um item de estoque. Qual o nome do item de estoque?",
  confirmLabel: "Criar item",
  requiredPermission: "inventory.manage_items",
};

const INVENTORY_EDIT_CONTEXT: FormChatContextConfig = {
  key: "inventory_edit",
  entityType: "inventory_item",
  title: "Assistente de estoque",
  subtitle: "Edição conversacional de estoque",
  initialMessage: "Carregando os dados do item…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "inventory.manage_items",
};

const ACTIVITY_CONTEXT: FormChatContextConfig = {
  key: "activity",
  entityType: "activity",
  title: "Assistente de atividades",
  subtitle: "Criação conversacional de atividade",
  initialMessage: "Oi! Vamos criar uma nova atividade. Qual o nome dela?",
  confirmLabel: "Criar atividade",
  requiredPermission: "activity.create",
  requiredPermissionOwn: "activity.create.own",
};

const ACTIVITY_EDIT_CONTEXT: FormChatContextConfig = {
  key: "activity_edit",
  entityType: "activity",
  title: "Assistente de atividades",
  subtitle: "Edição conversacional de atividade",
  initialMessage: "Carregando os dados da atividade…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "activity.update",
  requiredPermissionOwn: "activity.update.own",
};

const ACTIVITY_MODEL_CONTEXT: FormChatContextConfig = {
  key: "activity_model",
  entityType: "activity",
  title: "Assistente de atividades",
  subtitle: "Nova atividade a partir de modelo",
  initialMessage: "Carregando o modelo da atividade…",
  confirmLabel: "Criar atividade",
  requiredPermission: "activity.create",
  requiredPermissionOwn: "activity.create.own",
};

const RITUAL_CONTEXT: FormChatContextConfig = {
  ...ACTIVITY_CONTEXT,
  activityType: "RITUAL",
  title: "Assistente de rituais",
  subtitle: "Criação conversacional de ritual",
  initialMessage: "Oi! Vamos criar um novo ritual. Qual o nome dele?",
  confirmLabel: "Criar ritual",
};

const RITUAL_EDIT_CONTEXT: FormChatContextConfig = {
  ...ACTIVITY_EDIT_CONTEXT,
  activityType: "RITUAL",
  title: "Assistente de rituais",
  subtitle: "Edição conversacional de ritual",
  initialMessage: "Carregando os dados do ritual…",
};

const RITUAL_MODEL_CONTEXT: FormChatContextConfig = {
  ...ACTIVITY_MODEL_CONTEXT,
  activityType: "RITUAL",
  title: "Assistente de rituais",
  subtitle: "Novo ritual a partir de modelo",
  initialMessage: "Carregando o modelo do ritual…",
  confirmLabel: "Criar ritual",
};

const EVENT_CONTEXT: FormChatContextConfig = {
  key: "event",
  entityType: "event",
  title: "Assistente de eventos",
  subtitle: "Criação conversacional de evento",
  initialMessage: "Oi! Vamos criar um novo evento. Selecione o tipo de evento.",
  initialActiveField: "type",
  confirmLabel: "Criar evento",
  requiredPermission: "event.create",
};

const EVENT_MODEL_CONTEXT: FormChatContextConfig = {
  key: "event_model",
  entityType: "event",
  title: "Assistente de eventos",
  subtitle: "Novo evento a partir de modelo",
  initialMessage: "Carregando o modelo do evento…",
  confirmLabel: "Criar evento",
  requiredPermission: "event.create",
};

const SPIRITUAL_PROFILE_CONTEXT: FormChatContextConfig = {
  key: "spiritual_profile",
  entityType: "spiritual_profile",
  title: "Assistente de perfil espiritual",
  subtitle: "Cadastro conversacional de linha ou entidade",
  initialMessage:
    "Oi! Vamos registrar uma linha ou entidade no seu perfil espiritual. Qual linha você quer cadastrar?",
  confirmLabel: "Adicionar registro",
  requiredPermission: "spiritual_profile.manage_self",
};

const INCOME_CONTEXT: FormChatContextConfig = {
  key: "income",
  entityType: "income",
  title: "Assistente financeiro",
  subtitle: "Registro conversacional de entrada",
  initialMessage:
    "Vou te ajudar a registrar uma entrada. Em qual categoria esse lançamento se encaixa?",
  initialActiveField: "categoryId",
  confirmLabel: "Registrar entrada",
  requiredPermission: "financial.create_income",
  requiredPermissionOwn: "financial.create_incomes.own",
};

const INCOME_EDIT_CONTEXT: FormChatContextConfig = {
  key: "income_edit",
  entityType: "income",
  title: "Assistente financeiro",
  subtitle: "Edição conversacional de entrada",
  initialMessage: "Carregando os dados da entrada…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "financial.update_income",
  requiredPermissionOwn: "financial.update_incomes.own",
};

const OUTCOME_CONTEXT: FormChatContextConfig = {
  key: "outcome",
  entityType: "outcome",
  title: "Assistente financeiro",
  subtitle: "Registro conversacional de saída",
  initialMessage:
    "Vou te ajudar a registrar uma saída. Em qual categoria esse lançamento se encaixa?",
  initialActiveField: "categoryId",
  confirmLabel: "Registrar saída",
  requiredPermission: "financial.create_expense",
  requiredPermissionOwn: "financial.create_outcomes.own",
};

const OUTCOME_EDIT_CONTEXT: FormChatContextConfig = {
  key: "outcome_edit",
  entityType: "outcome",
  title: "Assistente financeiro",
  subtitle: "Edição conversacional de saída",
  initialMessage: "Carregando os dados da saída…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "financial.update_outcome",
  requiredPermissionOwn: "financial.update_outcomes.own",
};

const MONTHLY_FEE_EDIT_CONTEXT: FormChatContextConfig = {
  key: "monthly_fee_edit",
  entityType: "monthly_fee",
  title: "Assistente financeiro",
  subtitle: "Edição conversacional da mensalidade",
  initialMessage: "Carregando os dados da mensalidade…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "financial.manage_accounts",
};

const BANK_ACCOUNT_CONTEXT: FormChatContextConfig = {
  key: "bank_account",
  entityType: "bank_account",
  title: "Assistente financeiro",
  subtitle: "Cadastro conversacional de conta bancária",
  initialMessage:
    "Vou te ajudar a cadastrar uma conta bancária. Qual é o banco?",
  initialActiveField: "bankAccountOptionId",
  confirmLabel: "Cadastrar conta",
  requiredPermission: "financial.manage_accounts",
  requiredPermissionOwn: "financial.manage_accounts_create.own",
};

const BANK_ACCOUNT_EDIT_CONTEXT: FormChatContextConfig = {
  key: "bank_account_edit",
  entityType: "bank_account",
  title: "Assistente financeiro",
  subtitle: "Edição conversacional de conta bancária",
  initialMessage: "Carregando os dados da conta bancária…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "financial.manage_accounts",
  requiredPermissionOwn: "financial.manage_accounts_update.own",
};

const RITUAL_BATH_CONTEXT: FormChatContextConfig = {
  key: "ritual_bath",
  entityType: "ritual_bath",
  title: "Assistente de banhos",
  subtitle: "Cadastro conversacional de banho ritual",
  initialMessage:
    "Vou te ajudar a cadastrar um banho ritual. Qual é a categoria do banho?",
  initialActiveField: "bathCategoryId",
  confirmLabel: "Cadastrar banho",
  requiredPermission: "ritual_bath.create",
  requiredPermissionOwn: "ritual_bath.create.own",
};

const RITUAL_BATH_EDIT_CONTEXT: FormChatContextConfig = {
  key: "ritual_bath_edit",
  entityType: "ritual_bath",
  title: "Assistente de banhos",
  subtitle: "Edição conversacional de banho ritual",
  initialMessage: "Carregando os dados do banho…",
  confirmLabel: "Salvar alterações",
  requiredPermission: "ritual_bath.update",
  requiredPermissionOwn: "ritual_bath.update.own",
};

const CONTEXT_MATCHERS: Array<{
  test: (pathname: string) => boolean;
  context: FormChatContextConfig;
}> = [
  {
    test: (pathname) => pathname.startsWith("/inventory"),
    context: INVENTORY_CONTEXT,
  },
];

const isActivityListRoute = (pathname: string) => pathname === "/activities";

const isActivityCreateRoute = (pathname: string) =>
  pathname === "/activities/new";

const isRitualListRoute = (pathname: string) => pathname === "/rituals";

const isRitualCreateRoute = (pathname: string) => pathname === "/rituals/new";

const isEventsListRoute = (pathname: string) => pathname === "/events";

const isEventCreateRoute = (pathname: string) => pathname === "/events/new";

const isInventoryCreateRoute = (pathname: string) =>
  pathname === "/inventory/new";

const isSpiritualProfileListRoute = (pathname: string) =>
  pathname === "/spiritual-profile";

const isSpiritualProfileCreateRoute = (pathname: string) =>
  pathname === "/spiritual-profile/new";

export const isFormChatCreateRoute = (pathname: string) =>
  isEventCreateRoute(pathname) ||
  isActivityCreateRoute(pathname) ||
  isRitualCreateRoute(pathname) ||
  isInventoryCreateRoute(pathname) ||
  isSpiritualProfileCreateRoute(pathname) ||
  Boolean(getFinancialCreateFlow(pathname)) ||
  isBankAccountCreateRoute(pathname) ||
  isRitualBathCreateRoute(pathname);

export const resolveFormChatContext = (
  pathname: string,
  search = "",
): FormChatContextConfig | null => {
  if (isFinancialFormChatEnabled()) {
    const createFlow = getFinancialCreateFlow(pathname, search);
    if (createFlow) {
      return createFlow === "outcome" ? OUTCOME_CONTEXT : INCOME_CONTEXT;
    }
    const editTarget = getFinancialEditTarget(pathname);
    if (editTarget) {
      return editTarget.flow === "outcome"
        ? OUTCOME_EDIT_CONTEXT
        : INCOME_EDIT_CONTEXT;
    }
    if (getMonthlyFeeEditTargetId(pathname)) {
      return MONTHLY_FEE_EDIT_CONTEXT;
    }
  }

  if (isBankAccountFormChatEnabled()) {
    if (isBankAccountCreateRoute(pathname)) return BANK_ACCOUNT_CONTEXT;
    if (getBankAccountEditTargetId(pathname)) return BANK_ACCOUNT_EDIT_CONTEXT;
  }

  if (isRitualBathFormChatEnabled()) {
    if (isRitualBathCreateRoute(pathname)) return RITUAL_BATH_CONTEXT;
    if (getRitualBathEditTargetId(pathname)) return RITUAL_BATH_EDIT_CONTEXT;
  }

  if (isEventCreateRoute(pathname)) {
    return EVENT_CONTEXT;
  }

  if (isEventFormChatEnabled() && isEventsListRoute(pathname)) {
    return EVENT_CONTEXT;
  }

  if (isEventFormChatEnabled() && getEventModelTargetId(pathname)) {
    return EVENT_MODEL_CONTEXT;
  }

  if (isActivityCreateRoute(pathname)) {
    return ACTIVITY_CONTEXT;
  }

  if (isRitualCreateRoute(pathname)) {
    return RITUAL_CONTEXT;
  }

  const isRitualRoute = getActivityTypeFromPath(pathname) === "RITUAL";

  if (isActivityFormChatEnabled() && getActivityEditTargetId(pathname)) {
    return isRitualRoute ? RITUAL_EDIT_CONTEXT : ACTIVITY_EDIT_CONTEXT;
  }

  if (isActivityFormChatEnabled() && getActivityModelTargetId(pathname)) {
    return isRitualRoute ? RITUAL_MODEL_CONTEXT : ACTIVITY_MODEL_CONTEXT;
  }

  if (isActivityFormChatEnabled() && isActivityListRoute(pathname)) {
    return ACTIVITY_CONTEXT;
  }

  if (isActivityFormChatEnabled() && isRitualListRoute(pathname)) {
    return RITUAL_CONTEXT;
  }

  if (isInventoryFormChatEnabled() && getInventoryEditTargetId(pathname)) {
    return INVENTORY_EDIT_CONTEXT;
  }

  if (isSpiritualProfileCreateRoute(pathname)) {
    return SPIRITUAL_PROFILE_CONTEXT;
  }

  if (
    isSpiritualFormChatEnabled() &&
    isSpiritualProfileListRoute(pathname)
  ) {
    return SPIRITUAL_PROFILE_CONTEXT;
  }

  const matcher = CONTEXT_MATCHERS.find((entry) => entry.test(pathname));
  return matcher ? matcher.context : null;
};

export const isActivityFormChatEnabled = () => {
  const activityFlag = import.meta.env.VITE_ACTIVITY_FORM_CHAT_ENABLED;
  if (activityFlag === "true") return true;
  if (activityFlag === "false") return false;
  return import.meta.env.VITE_FORM_CHAT_ENABLED === "true";
};

export const isEventFormChatEnabled = () => {
  const eventFlag = import.meta.env.VITE_EVENT_FORM_CHAT_ENABLED;
  if (eventFlag === "true") return true;
  if (eventFlag === "false") return false;
  return import.meta.env.VITE_FORM_CHAT_ENABLED === "true";
};

export const isInventoryFormChatEnabled = () =>
  import.meta.env.VITE_FORM_CHAT_ENABLED === "true";

export const isSpiritualFormChatEnabled = () => {
  const spiritualFlag = import.meta.env.VITE_SPIRITUAL_PROFILE_FORM_CHAT_ENABLED;
  if (spiritualFlag === "true") return true;
  if (spiritualFlag === "false") return false;
  return import.meta.env.VITE_FORM_CHAT_ENABLED === "true";
};

export const isFinancialFormChatEnabled = () => {
  const financialFlag = import.meta.env.VITE_FINANCIAL_FORM_CHAT_ENABLED;
  if (financialFlag === "true") return true;
  if (financialFlag === "false") return false;
  return import.meta.env.VITE_FORM_CHAT_ENABLED === "true";
};

export const isBankAccountFormChatEnabled = () => {
  const bankAccountFlag = import.meta.env?.VITE_BANK_ACCOUNT_FORM_CHAT_ENABLED;
  if (bankAccountFlag === "true") return true;
  if (bankAccountFlag === "false") return false;
  return import.meta.env?.VITE_FORM_CHAT_ENABLED === "true";
};

export const isRitualBathFormChatEnabled = () => {
  const ritualBathFlag = import.meta.env?.VITE_RITUAL_BATH_FORM_CHAT_ENABLED;
  if (ritualBathFlag === "true") return true;
  if (ritualBathFlag === "false") return false;
  return import.meta.env?.VITE_FORM_CHAT_ENABLED === "true";
};

export const isTicketingFormChatEnabled = () => {
  const ticketingFlag = import.meta.env.VITE_TICKETING_FORM_CHAT_ENABLED;
  if (ticketingFlag === "true") return true;
  if (ticketingFlag === "false") return false;
  return import.meta.env.VITE_FORM_CHAT_ENABLED === "true";
};

export const isProfileFormChatEnabled = () => {
  const profileFlag = import.meta.env?.VITE_PROFILE_FORM_CHAT_ENABLED;
  if (profileFlag === "true") return true;
  if (profileFlag === "false") return false;
  return import.meta.env?.VITE_FORM_CHAT_ENABLED === "true";
};

export const isTerreiroProfileFormChatEnabled = () => {
  const terreiroFlag = import.meta.env?.VITE_TERREIRO_PROFILE_FORM_CHAT_ENABLED;
  if (terreiroFlag === "true") return true;
  if (terreiroFlag === "false") return false;
  return import.meta.env?.VITE_FORM_CHAT_ENABLED === "true";
};
